const EXECUTION_ANNOTATION_VERTICAL_DROPS = [35, 52, 70, 90]
const EXECUTION_ANNOTATION_DIAGONAL_LENGTH = 55
const EXECUTION_ANNOTATION_DIAGONAL_ANGLE_DEG = 50
const EXECUTION_ANNOTATION_CANDLE_PADDING = 6
const EXECUTION_ANNOTATION_CANDLE_CLEARANCE = 12
const EXECUTION_MARKER_ICON_RADIUS = 7
const EXECUTION_MARKER_HIT_RADIUS = 12
const EXECUTION_MARKER_HOVER_SCALE = 1.15
const EXECUTION_MARKER_HOVER_ANIMATION_MS = 260
const EXECUTION_MARKER_NEUTRAL_BY_THEME = Object.freeze({
  dark: Object.freeze({
    leader: 'rgba(255,255,255,0.30)',
    icon: 'rgba(255,255,255,0.45)',
    fill: 'rgba(9,12,18,0.72)',
  }),
  light: Object.freeze({
    leader: 'rgba(156,163,175,0.70)',
    icon: 'rgba(156,163,175,0.95)',
    fill: 'rgba(255,255,255,0.82)',
  }),
})

function finiteNumber(value) {
  if (value == null || typeof value === 'boolean' || (typeof value === 'string' && value.trim() === '')) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

// Candles are start-labelled intervals. Never pick a future candle, bridge a
// missing interval, or clamp an out-of-range execution onto the loaded history.
function containingExecutionBar(marker, bars, timeframe) {
  const explicitTime = finiteNumber(marker?.bar_time_ref)
  const target = explicitTime > 0 ? explicitTime : finiteNumber(marker?.time)
  if (target == null || target <= 0) return null
  const duration = { '1min': 60, '2min': 120, '5min': 300, '15min': 900,
    '30min': 1800, '65min': 3900, daily: 86400, weekly: 604800 }[timeframe]
  let candidate = null
  for (const bar of bars) {
    const time = finiteNumber(bar?.time)
    if (time == null || time <= 0 || time > target) continue
    if (time === target) return bar
    if (explicitTime > 0 || !duration || target >= time + duration) continue
    if (!candidate || time > Number(candidate.time)) candidate = bar
  }
  return candidate
}

function executionAnnotationAnchorY({ isBuy, priceY, bar, series }) {
  const priceAnchor = isBuy ? priceY + 4 : priceY - 4
  const high = finiteNumber(bar?.high)
  const low = finiteNumber(bar?.low)
  if (high == null || low == null || !series) return priceAnchor

  const highY = series.priceToCoordinate(high)
  const lowY = series.priceToCoordinate(low)
  if (highY == null || lowY == null) return priceAnchor

  const candleTop = Math.min(highY, lowY)
  const candleBottom = Math.max(highY, lowY)
  return isBuy
    ? Math.max(priceAnchor, candleBottom + EXECUTION_ANNOTATION_CANDLE_CLEARANCE)
    : Math.min(priceAnchor, candleTop - EXECUTION_ANNOTATION_CANDLE_CLEARANCE)
}

function buildExecutionAnnotationCandleBoxes({ chart, series, bars, minX, maxX }) {
  if (!chart || !series || !Array.isArray(bars)) return []
  const projected = []
  for (const bar of bars) {
    const time = finiteNumber(bar?.time)
    const high = finiteNumber(bar?.high)
    const low = finiteNumber(bar?.low)
    if (time == null || high == null || low == null) continue

    const x = chart.timeScale().timeToCoordinate(time)
    if (x == null || x < minX || x > maxX) continue

    const highY = series.priceToCoordinate(high)
    const lowY = series.priceToCoordinate(low)
    if (highY == null || lowY == null) continue

    projected.push({
      x,
      top: Math.min(highY, lowY),
      bottom: Math.max(highY, lowY),
    })
  }

  return projected.map((box) => ({
    left: box.x - EXECUTION_ANNOTATION_CANDLE_PADDING,
    right: box.x + EXECUTION_ANNOTATION_CANDLE_PADDING,
    top: box.top - EXECUTION_ANNOTATION_CANDLE_PADDING,
    bottom: box.bottom + EXECUTION_ANNOTATION_CANDLE_PADDING,
  }))
}

function segmentIntersectsBox(a, b, box) {
  const left = Math.min(a.x, b.x)
  const right = Math.max(a.x, b.x)
  const top = Math.min(a.y, b.y)
  const bottom = Math.max(a.y, b.y)
  return right >= box.left && left <= box.right && bottom >= box.top && top <= box.bottom
}

function executionAnnotationRouteCollides({ anchorX, anchorY, elbowX, elbowY, iconX, iconY, boxes }) {
  if (!Array.isArray(boxes) || boxes.length === 0) return false
  const anchor = { x: anchorX, y: anchorY }
  const elbow = { x: elbowX, y: elbowY }
  const icon = { x: iconX, y: iconY }
  return boxes.some((box) => segmentIntersectsBox(anchor, elbow, box) || segmentIntersectsBox(elbow, icon, box))
}

function chooseExecutionAnnotationRoute({ isBuy, anchorX, anchorY, horizontalSign, diagDx, diagDy, boxes }) {
  const elbowX = anchorX
  for (const drop of EXECUTION_ANNOTATION_VERTICAL_DROPS) {
    const elbowY = isBuy ? anchorY + drop : anchorY - drop
    const iconX = elbowX + horizontalSign * diagDx
    const iconY = isBuy ? elbowY + diagDy : elbowY - diagDy
    if (!executionAnnotationRouteCollides({ anchorX, anchorY, elbowX, elbowY, iconX, iconY, boxes })) {
      return { elbowX, elbowY, iconX, iconY }
    }
  }

  const fallbackDrop = EXECUTION_ANNOTATION_VERTICAL_DROPS[EXECUTION_ANNOTATION_VERTICAL_DROPS.length - 1]
  const elbowY = isBuy ? anchorY + fallbackDrop : anchorY - fallbackDrop
  return {
    elbowX,
    elbowY,
    iconX: elbowX + horizontalSign * diagDx,
    iconY: isBuy ? elbowY + diagDy : elbowY - diagDy,
  }
}

function neutralMarkerTheme(theme) {
  return theme === 'light' ? EXECUTION_MARKER_NEUTRAL_BY_THEME.light : EXECUTION_MARKER_NEUTRAL_BY_THEME.dark
}

function markerColor(marker, bright = false, theme = 'dark') {
  if (!bright) return neutralMarkerTheme(theme).icon
  const alpha = 0.95
  if (marker?.type === 'stop-hit') return `rgba(201,127,42,${alpha})`
  if (marker?.type === 'target-hit') return `rgba(224,90,74,${alpha})`
  if (marker?.side === 'buy') return `rgba(74,170,106,${alpha})`
  if (marker?.side === 'sell') return `rgba(224,90,74,${alpha})`
  return `rgba(138,146,160,${alpha})`
}

function markerGlyph(marker) {
  if (marker?.type === 'stop-hit') return '!'
  if (marker?.side === 'buy') return '\u2191'
  if (marker?.side === 'sell') return '\u2193'
  return '\u2691'
}

function markerId(marker) {
  return marker?.id == null ? null : String(marker.id)
}

function hoverPulse(progress) {
  const clamped = Math.max(0, Math.min(1, progress))
  return Math.sin(clamped * Math.PI)
}

function easeOutCubic(progress) {
  const clamped = Math.max(0, Math.min(1, progress))
  return 1 - ((1 - clamped) ** 3)
}

function distance(a, b) {
  return Math.sqrt(((b.x - a.x) ** 2) + ((b.y - a.y) ** 2))
}

function interpolatePoint(a, b, progress) {
  return {
    x: a.x + (b.x - a.x) * progress,
    y: a.y + (b.y - a.y) * progress,
  }
}

function buildDirectionalGlowPath(annotation, progress) {
  const route = [
    { x: annotation.iconX, y: annotation.iconY },
    { x: annotation.elbowX, y: annotation.elbowY },
    { x: annotation.anchorX, y: annotation.anchorY },
  ]
  const totalLength = distance(route[0], route[1]) + distance(route[1], route[2])
  if (!Number.isFinite(totalLength) || totalLength <= 0) return []

  let remaining = totalLength * Math.max(0, Math.min(1, progress))
  const points = [route[0]]
  for (let i = 0; i < route.length - 1; i += 1) {
    const start = route[i]
    const end = route[i + 1]
    const segmentLength = distance(start, end)
    if (!Number.isFinite(segmentLength) || segmentLength <= 0) continue
    if (remaining >= segmentLength) {
      points.push(end)
      remaining -= segmentLength
      continue
    }
    if (remaining > 0) {
      points.push(interpolatePoint(start, end, remaining / segmentLength))
    }
    break
  }
  return points
}

function makeHitBoxes(annotation) {
  const iconBox = {
    kind: 'icon',
    annotation,
    left: annotation.iconX - EXECUTION_MARKER_HIT_RADIUS,
    right: annotation.iconX + EXECUTION_MARKER_HIT_RADIUS,
    top: annotation.iconY - EXECUTION_MARKER_HIT_RADIUS,
    bottom: annotation.iconY + EXECUTION_MARKER_HIT_RADIUS,
  }
  const leaderBox = {
    kind: 'leader',
    annotation,
    left: Math.min(annotation.anchorX, annotation.elbowX, annotation.iconX) - 5,
    right: Math.max(annotation.anchorX, annotation.elbowX, annotation.iconX) + 5,
    top: Math.min(annotation.anchorY, annotation.elbowY, annotation.iconY) - 5,
    bottom: Math.max(annotation.anchorY, annotation.elbowY, annotation.iconY) + 5,
  }
  return [iconBox, leaderBox]
}

function pointInBox(point, box) {
  return point.x >= box.left && point.x <= box.right && point.y >= box.top && point.y <= box.bottom
}

export class ExecutionMarkerPrimitive {
  constructor() {
    this._data = { markers: [], bars: [], theme: 'dark' }
    this._chart = null
    this._series = null
    this._requestUpdate = null
    this._annotations = []
    this._hitBoxes = []
    this._hoveredMarkerId = null
    this._selectedMarkerIds = new Set()
    this._hoverStartedAt = 0
    this._hoverRafId = null
    this._paneViews = [new ExecutionMarkerPaneView(this)]
  }

  attached(param) {
    this._chart = param.chart
    this._series = param.series
    this._requestUpdate = param.requestUpdate
    this._rebuildAnnotations()
  }

  detached() {
    this._chart = null
    this._series = null
    this._requestUpdate = null
    this._data = { markers: [], bars: [], theme: 'dark' }
    this._annotations = []
    this._hitBoxes = []
    this._hoveredMarkerId = null
    this._selectedMarkerIds = new Set()
    this._hoverStartedAt = 0
    this._cancelHoverAnimation()
  }

  update(next) {
    this._data = {
      markers: Array.isArray(next?.markers) ? next.markers : [],
      bars: Array.isArray(next?.bars) ? next.bars : [],
      theme: next?.theme === 'light' ? 'light' : 'dark',
      timeframe: next?.timeframe,
    }
    this._rebuildAnnotations()
    if (typeof this._requestUpdate === 'function') this._requestUpdate()
  }

  setHoveredMarkerId(id) {
    const next = id == null ? null : String(id)
    if (this._hoveredMarkerId === next) return
    this._hoveredMarkerId = next
    this._hoverStartedAt = next == null ? 0 : this._now()
    if (typeof this._requestUpdate === 'function') this._requestUpdate()
    if (next == null) {
      this._cancelHoverAnimation()
    } else {
      this._scheduleHoverAnimation()
    }
  }

  setSelectedMarkerIds(ids) {
    const next = new Set((Array.isArray(ids) ? ids : []).map((id) => String(id)))
    const sameValues =
      next.size === this._selectedMarkerIds.size &&
      [...next].every((id) => this._selectedMarkerIds.has(id))
    if (sameValues) return
    this._selectedMarkerIds = next
    if (typeof this._requestUpdate === 'function') this._requestUpdate()
  }

  updateAllViews() { /* renderer reads live state */ }

  paneViews() {
    return this._paneViews
  }

  annotations() {
    this._rebuildAnnotations()
    return this._annotations
  }

  hitTest(point) {
    if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return null
    this._rebuildAnnotations()
    const box = this._hitBoxes.find((candidate) => pointInBox(point, candidate))
    return box?.annotation ?? null
  }

  _rebuildAnnotations() {
    const chart = this._chart
    const series = this._series
    const markers = this._data.markers
    const bars = this._data.bars
    if (!chart || !series || !Array.isArray(markers) || markers.length === 0) {
      this._annotations = []
      this._hitBoxes = []
      return
    }

    const angleRad = (EXECUTION_ANNOTATION_DIAGONAL_ANGLE_DEG * Math.PI) / 180
    const diagDx = Math.cos(angleRad) * EXECUTION_ANNOTATION_DIAGONAL_LENGTH
    const diagDy = Math.sin(angleRad) * EXECUTION_ANNOTATION_DIAGONAL_LENGTH
    const next = []

    for (const marker of markers) {
      const price = finiteNumber(marker?.price)
      const rawTime = finiteNumber(marker?.bar_time_ref ?? marker?.time)
      if (price == null || rawTime == null) continue

      const anchorBar = containingExecutionBar(marker, bars, this._data.timeframe)
      if (!anchorBar) continue
      const x = chart.timeScale().timeToCoordinate(Number(anchorBar.time))
      const priceY = series.priceToCoordinate(price)
      if (!Number.isFinite(x) || !Number.isFinite(priceY) || price <= 0) continue

      const isBuy = marker.side === 'buy'
      const anchorY = executionAnnotationAnchorY({ isBuy, priceY, bar: anchorBar, series })
      const horizontalSign = x - diagDx < 16 ? 1 : -1
      const boxes = buildExecutionAnnotationCandleBoxes({
        chart,
        series,
        bars,
        minX: Math.min(x, x + horizontalSign * diagDx) - 12,
        maxX: Math.max(x, x + horizontalSign * diagDx) + 12,
      })
      const route = chooseExecutionAnnotationRoute({
        isBuy,
        anchorX: x,
        anchorY,
        horizontalSign,
        diagDx,
        diagDy,
        boxes,
      })
      next.push({
        marker,
        isBuy,
        anchorX: x,
        anchorY,
        ...route,
      })
    }

    const clustered = []
    const used = new Set()
    for (let i = 0; i < next.length; i += 1) {
      if (used.has(i)) continue
      const anchor = next[i]
      const cluster = [anchor]
      used.add(i)
      for (let j = i + 1; j < next.length; j += 1) {
        if (used.has(j)) continue
        const other = next[j]
        const dx = anchor.iconX - other.iconX
        const dy = anchor.iconY - other.iconY
        if (Math.sqrt(dx * dx + dy * dy) < 24) {
          cluster.push(other)
          used.add(j)
        }
      }
      if (cluster.length === 1) {
        clustered.push(anchor)
      } else {
        const avgIconX = cluster.reduce((sum, item) => sum + item.iconX, 0) / cluster.length
        const avgIconY = cluster.reduce((sum, item) => sum + item.iconY, 0) / cluster.length
        clustered.push({
          ...cluster[cluster.length - 1],
          iconX: avgIconX,
          iconY: avgIconY,
          _cluster: cluster,
          _clusterCount: cluster.length,
        })
      }
    }

    this._annotations = clustered
    this._hitBoxes = clustered.flatMap(makeHitBoxes)
  }

  _now() {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now()
    }
    return Date.now()
  }

  _cancelHoverAnimation() {
    if (this._hoverRafId == null) return
    if (typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this._hoverRafId)
    }
    this._hoverRafId = null
  }

  _scheduleHoverAnimation() {
    if (this._hoverRafId != null) return
    if (typeof requestAnimationFrame !== 'function') return

    const tick = () => {
      this._hoverRafId = null
      if (!this._hoveredMarkerId || !this._hoverStartedAt) return
      if (typeof this._requestUpdate === 'function') this._requestUpdate()
      if (this._now() - this._hoverStartedAt < EXECUTION_MARKER_HOVER_ANIMATION_MS) {
        this._hoverRafId = requestAnimationFrame(tick)
      }
    }
    this._hoverRafId = requestAnimationFrame(tick)
  }
}

class ExecutionMarkerPaneView {
  constructor(primitive) {
    this._renderer = new ExecutionMarkerPaneRenderer(primitive)
  }

  zOrder() { return 'top' }

  renderer() { return this._renderer }
}

class ExecutionMarkerPaneRenderer {
  constructor(primitive) {
    this._primitive = primitive
  }

  draw(target) {
    const annotations = this._primitive.annotations()
    if (!annotations.length) return

    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context
      const hpr = scope.horizontalPixelRatio || 1
      const vpr = scope.verticalPixelRatio || 1

      for (const annotation of annotations) {
        const neutral = neutralMarkerTheme(this._primitive._data.theme)
        const id = markerId(annotation.marker)
        const hovered = id === this._primitive._hoveredMarkerId
        const selected = id != null && this._primitive._selectedMarkerIds.has(id)
        const active = hovered || selected
        const stroke = active ? markerColor(annotation.marker, true, this._primitive._data.theme) : neutral.leader
        const iconColor = markerColor(annotation.marker, active, this._primitive._data.theme)
        const elapsed = hovered && this._primitive._hoverStartedAt
          ? this._primitive._now() - this._primitive._hoverStartedAt
          : 0
        const progress = elapsed / EXECUTION_MARKER_HOVER_ANIMATION_MS
        const pulse = hovered ? hoverPulse(progress) : 0
        const iconScale = active ? EXECUTION_MARKER_HOVER_SCALE : 1
        const glowBlur = hovered ? (3 + pulse * 5) * Math.max(hpr, vpr) : 0

        ctx.save()
        ctx.beginPath()
        ctx.moveTo(annotation.anchorX * hpr, annotation.anchorY * vpr)
        ctx.lineTo(annotation.elbowX * hpr, annotation.elbowY * vpr)
        ctx.lineTo(annotation.iconX * hpr, annotation.iconY * vpr)
        ctx.strokeStyle = stroke
        ctx.lineWidth = (active ? 2 : 1.25) * Math.max(hpr, vpr)
        ctx.stroke()
        ctx.restore()

        if (hovered) {
          const glowPath = buildDirectionalGlowPath(annotation, easeOutCubic(progress))
          if (glowPath.length >= 2) {
            ctx.save()
            ctx.beginPath()
            ctx.moveTo(glowPath[0].x * hpr, glowPath[0].y * vpr)
            for (let i = 1; i < glowPath.length; i += 1) {
              ctx.lineTo(glowPath[i].x * hpr, glowPath[i].y * vpr)
            }
            ctx.strokeStyle = iconColor
            ctx.lineWidth = 2.25 * Math.max(hpr, vpr)
            ctx.shadowColor = iconColor
            ctx.shadowBlur = glowBlur
            ctx.stroke()
            ctx.restore()
          }
        }

        ctx.save()
        ctx.beginPath()
        ctx.arc(
          annotation.iconX * hpr,
          annotation.iconY * vpr,
          EXECUTION_MARKER_ICON_RADIUS * iconScale * Math.max(hpr, vpr),
          0,
          Math.PI * 2
        )
        ctx.fillStyle = neutral.fill
        ctx.fill()
        ctx.strokeStyle = iconColor
        ctx.lineWidth = 1.5 * Math.max(hpr, vpr)
        if (hovered) {
          ctx.shadowColor = iconColor
          ctx.shadowBlur = glowBlur
        }
        ctx.stroke()
        ctx.fillStyle = iconColor
        ctx.font = `600 ${12 * vpr}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(markerGlyph(annotation.marker), annotation.iconX * hpr, annotation.iconY * vpr)
        ctx.restore()
      }
    })
  }
}
